import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Taxes() {
  const [taxes, setTaxes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", amount: 0, due_date: "", status: "due" });

  const load = () => api.get("/taxes").then(r => setTaxes(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.due_date) return toast.error("Name and due date required");
    await api.post("/taxes", { ...form, amount: Number(form.amount) });
    toast.success("Tax obligation added");
    setOpen(false);
    setForm({ name: "", amount: 0, due_date: "", status: "due" });
    load();
  };

  const markPaid = async (id) => {
    await api.patch(`/taxes/${id}`, { status: "paid" });
    load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = taxes.filter(t => t.status !== "paid").sort((a, b) => a.due_date.localeCompare(b.due_date));
  const totalDue = upcoming.reduce((s, t) => s + Number(t.amount), 0);
  const overdue = taxes.filter(t => t.status !== "paid" && t.due_date < today);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tax Management"
        subtitle="Nigerian tax compliance — PAYE, VAT, and company income tax"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-tax-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Obligation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New tax obligation</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. VAT — Q1 2026" data-testid="tax-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (₦)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} /></div>
                  <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="due">Due</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-tax-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {overdue.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-red-700">{overdue.length} overdue tax {overdue.length > 1 ? "filings" : "filing"}</div>
            <div className="text-xs text-red-600">Total: {formatNaira(overdue.reduce((s, t) => s + t.amount, 0))} — file immediately to avoid penalties.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Total Outstanding</div><div className="font-heading font-bold text-2xl text-slate-900 num">{formatNaira(totalDue)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Upcoming Filings</div><div className="font-heading font-bold text-2xl text-slate-900 num">{upcoming.length}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Overdue</div><div className="font-heading font-bold text-2xl text-red-600 num">{overdue.length}</div></div>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-4 py-3">Obligation</th>
              <th className="text-left px-4 py-3">Due date</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {taxes.map(t => {
              const isOverdue = t.status !== "paid" && t.due_date < today;
              return (
                <tr key={t.id} className="row" data-testid={`tax-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(t.due_date)}</td>
                  <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(t.amount)}</td>
                  <td className="px-4 py-3"><Chip variant={statusChip(isOverdue ? "overdue" : t.status)}>{isOverdue ? "overdue" : t.status}</Chip></td>
                  <td className="px-4 py-3 text-right">
                    {t.status !== "paid" && <Button size="sm" variant="outline" onClick={() => markPaid(t.id)} data-testid={`pay-tax-${t.id}`}>Mark paid</Button>}
                  </td>
                </tr>
              );
            })}
            {taxes.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-500 text-sm">No tax obligations tracked.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
